import { IonButton, IonButtons, IonLabel, IonToolbar } from '@ionic/react';
import { HomeOutlined, SportsEsportsOutlined, WorkOutline } from '@material-ui/icons';
import React, { useEffect } from 'react';
import { matchPath, useLocation } from 'react-router';
import { PermissionGuard } from 'src/utils/PermissionGuard';
import StoneAgeOutlineWhite from '../assets/icons/stoneage-outline-white.svg';
import StoneAgeOutlinePrimary from '../assets/icons/stoneage-outline-primary.svg';
import StoneAgeOutline from '../assets/icons/stoneage-outline.svg';
import './MobileNavBar.scss';
import { useResolvedTheme } from 'src/theme/useResolvedTheme';
import { AppRouteConstants } from '../pages/app-layout/AppRouteConstants';
import { link, unlink } from 'ionicons/icons';
import { BleConnectionState } from 'src/redux/ble/constants';
import { Badge } from './reusable-components/Badge';
import { selectBleConnectionState } from 'src/redux/ble/selectors';
import { useSelector } from 'react-redux';

const isDev = process.env.REACT_APP_ENV !== 'production';

export const MobileNavBar: React.FC = () => {
  const [controllerClicked, setControllerClicked] = React.useState(0);
  const location = useLocation();
  const theme = useResolvedTheme();
  const bleState = useSelector(selectBleConnectionState);
  const isConnected = bleState === BleConnectionState.CONNECTED;
  const connectionBadgeIcon = isConnected ? link : unlink;
  const connectionColor = isConnected ? 'secondary' : 'medium';

  useEffect(() => {
    setControllerClicked(0);
  }, [location.pathname]);

  const controllerRoute = React.useMemo(
    () => (isDev && controllerClicked >= 2 ? '/controllers-dev' : '/controllers'),
    [controllerClicked],
  );

  const matches = React.useMemo(() => {
    const getMatch = (route: string) => matchPath(location.pathname, { path: route, exact: false, sensitive: false });
    return {
      home: !!getMatch('/home'),
      jobs: !!getMatch('/jobs'),
      controllers: !!getMatch('/controllers'),
      stoneAge: !!getMatch('/stone-age'),
    };
  }, [location.pathname]);

  const stoneAgeIcon = React.useMemo(() => {
    if (matches.stoneAge) {
      return StoneAgeOutlinePrimary;
    } else if (theme === 'dark') {
      return StoneAgeOutlineWhite;
    } else return StoneAgeOutline;
  }, [matches.stoneAge, theme]);

  return (
    <IonToolbar>
      <IonButtons className="mobile-toolbar">
        <IonButton routerLink="/home" routerDirection="root" color={matches.home ? 'primary' : 'medium'}>
          <div className="mobile-button-content">
            <HomeOutlined />
            <IonLabel className="label">Home</IonLabel>
          </div>
        </IonButton>
        <PermissionGuard permission="ViewJobLogs">
          <IonButton
            routerLink={AppRouteConstants.LogsList}
            routerDirection="root"
            color={matches.jobs ? 'primary' : 'medium'}
          >
            <div className="mobile-button-content">
              <WorkOutline />
              <IonLabel className="label">Jobs</IonLabel>
            </div>
          </IonButton>
        </PermissionGuard>
        <PermissionGuard permission="ViewControllers">
          <IonButton
            routerLink={controllerRoute}
            routerDirection="root"
            color={matches.controllers ? 'primary' : 'medium'}
            onClick={() => setControllerClicked(s => s + 1)}
            className="mobile-button"
          >
            <div className="mobile-button-content ">
              <div className="mobile-button-content-icon">
                <SportsEsportsOutlined />
                <div className="mobile-button-badge">
                  <Badge isActive={isConnected} icon={connectionBadgeIcon} color={connectionColor} />
                </div>
              </div>

              <IonLabel className="label">Controllers</IonLabel>
            </div>
          </IonButton>
        </PermissionGuard>

        <IonButton routerLink="/stone-age" routerDirection="root" color={matches.stoneAge ? 'primary' : 'medium'}>
          <div className="mobile-button-content">
            <img src={stoneAgeIcon} alt="StoneAge logo" />
            <IonLabel className="label">StoneAge</IonLabel>
          </div>
        </IonButton>
      </IonButtons>
    </IonToolbar>
  );
};

export default MobileNavBar;
